import React from 'react';
import PropTypes from 'prop-types';
import requiredIf from 'react-required-if';
import { LocalForm, Control, Errors } from 'react-redux-form';
import { difference } from 'lodash';
import {
  colours,
  componentHelper,
  messaging,
  notifications as notificationsHelper,
  unicode
} from '../helpers';
import {
  Badge,
  Button,
  Checkbox,
  Col,
  FormGroup,
  OverlayTrigger,
  Popover,
  Row
} from 'react-bootstrap';
import CollapsiblePanelContainer from '../Containers/CollapsiblePanelContainer';
import ConditionalComponent from './ConditionalComponent';


const getPopover = (title, body) => (
  <Popover
    id="popover-positioned-bottom"
    title={title}
    data-html
  >
    {body}
  </Popover>
);

const FavoriteCheckBox = (props) => (
  <Control.custom
    {...props}
    component={Checkbox}
    model=".isFavorite"
    mapProps={({ modelValue, onChange }) => {
      return {
        onChange: c => onChange(c.target.checked),
        checked: modelValue
      };
    }}
  >
    Mark Favorite
  </Control.custom>
);

const MuteCheckBox = (props) => (
  <Control.custom
    {...props}
    component={Checkbox}
    model=".isMuted"
    mapProps={({ modelValue, onChange }) => {
      return {
        onChange: c => onChange(c.target.checked),
        checked: modelValue
      };
    }}
  >
    <b> Mute Passed </b>
    | Detailed notifications will filter out passed verdicts,
    you will only receive notifications of failures
  </Control.custom>
);

const SummaryCheckbox = (props) => (
  <Control.custom
    {...props}
    component={Checkbox}
    model=".ruleTypes"
    mapProps={({ modelValue, onChange }) => {
      const value = notificationsHelper.RULETYPES.SUMMARY;
      return {
        onChange: c =>
          onChange(componentHelper.getTogglesValue(
            modelValue,
            value,
            c.target.checked
          )),
        checked: componentHelper.getChecked(modelValue, value)
      };
    }}
  >
    <b> Summary </b>
    | A single notification summarizing all jobs when the dispatch completes
  </Control.custom>
);

const DetailedCheckbox = (props) => (
  <Control.custom
    {...props}
    component={Checkbox}
    model=".ruleTypes"
    mapProps={({ modelValue, onChange }) => {
      const value = notificationsHelper.RULETYPES.DETAILED;
      return {
        onChange: c =>
          onChange(componentHelper.getTogglesValue(
            modelValue,
            value,
            c.target.checked
          )),
        checked: componentHelper.getChecked(modelValue, value)
      };
    }}
  >
    <b> Detailed </b> | A notification for each child job when it completes
  </Control.custom>
);

const EmailCheckbox = (props) => (
  <Control.custom
    {...props}
    component={Checkbox}
    model=".ruleTypes"
    mapProps={({ modelValue, onChange }) => {
      const value = notificationsHelper.RULETYPES.EMAIL;
      return {
        onChange: c =>
          onChange(componentHelper.getTogglesValue(
            modelValue,
            value,
            c.target.checked
          )),
        checked: componentHelper.getChecked(modelValue, value)
      };
    }}
  >
    <b> Email </b> | Enter a cron expression to receive a Summary by email
  </Control.custom>
);

const CronInput = (props) => (
  <div style={{ display: 'inline' }}>
    <Control.custom
      {...props}
      id="cron-input"
      model="."
      type="text"
      mapProps={({ modelValue, onChange }) => {
        const isChecked = componentHelper.getChecked(
          modelValue.ruleTypes,
          notificationsHelper.RULETYPES.EMAIL
        );

        return {
          value: isChecked ? modelValue.cron : '',
          disabled: !isChecked,
          onChange: i => onChange({
            ...modelValue,
            cron: i.target.value
          }),
          placeholder: isChecked ?
            '0 17 * * * { At 5pm every day }' :
            ''
        };
      }}
    />
  </div>
);

const TriggeredByInput = (props) => {
  return (
    <div style={{ display:'inline' }}>
      <Control.custom
        {...props}
        id="triggered-input"
        model=".triggeredBy"
        type="text"
        style={{ textTransform: 'lowercase' }}
        mapProps={({ modelValue, onChange }) => {
          return {
            value: modelValue,
            onChange: i => {
              const input = i.target.value.toLowerCase();
              onChange(input !== '' ? input.split(',') : []);
            }
          };
        }}
      />
      <OverlayTrigger
        trigger="click"
        rootClose
        overlay=
          {getPopover(
            'How do trigger filters work?',
            messaging.PROFILE_TRIGGER_POPOVER_BODY)}
      >
        <Button style={{ display: 'inline' }}>?</Button>
      </OverlayTrigger>
      <Control.custom
        model=".triggeredBy"
        component={Badge}
        style={{ background: colours.colours.blue, marginLeft: '5px' }}
        mapProps={({ modelValue }) => {
          const filterCount = modelValue.length;
          return {
            children: (
              <ConditionalComponent condition={filterCount > 0}>
                {`Found ${filterCount}.`}
              </ConditionalComponent>
            )
          };
        }}
      />
    </div>
  );
};

export default class NotificationsSelectorComponent extends React.Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onUnsubscribe: PropTypes.func.isRequired,
    config: requiredIf(
      PropTypes.object,
      props => props.isSubscribed),
    isSubscribed: PropTypes.bool.isRequired,
    isFavorite: PropTypes.bool.isRequired,
    request: PropTypes.shape({
      'rule_id': PropTypes.number.isRequired,
      'recipient': PropTypes.string.isRequired,
      'rule_types': PropTypes.number.isRequired,
      'is_single': PropTypes.bool.isRequired,
      'single_id': PropTypes.number.isRequired,
      'config': PropTypes.object
    }),
    rule: PropTypes.shape({
      id: PropTypes.number.isRequired,
      'rule_name': PropTypes.string.isRequired,
      description: PropTypes.string.isRequired
    }).isRequired
  };

  constructor() {
    super();
    this.state = { hasUnsavedChanges: false };
  }

  checkUnsavedChanges = (request, modelValue) => {
    const config = request && request.config;
    const source = config ?
      {
        ...config,
        'email_cron': config.email_cron || '',
        'triggered_by_filter': config.triggered_by_filter || [],
        'rule_types': request.rule_types
      } :
      {
        'is_favorite': false,
        'is_muted': false,
        'email_cron': '',
        'triggered_by_filter': [],
        'rule_types': 0
      };

    return source.is_favorite !== modelValue.isFavorite ||
      source.is_muted !== modelValue.isMuted ||
      source.email_cron !== modelValue.cron ||
      source.rule_types !== modelValue.ruleTypes ||
      source.triggered_by_filter.length !== modelValue.triggeredBy.length ||
      difference(modelValue.triggeredBy, source.triggered_by_filter).length > 0;
  };

  render() {
    const {
      onSubmit,
      onUnsubscribe,
      isSubscribed,
      isFavorite,
      request,
      rule
    } = this.props;

    const initialState = {
      ruleId: rule.id,
      isFavorite: request && request.config.is_favorite || false,
      isMuted: request && request.config.is_muted || false,
      ruleTypes: request ? request.rule_types : 0,
      cron: request && request.config.email_cron || '',
      triggeredBy: request && request.config.triggered_by_filter || []
    };

    // slice off type prefix
    const ruleName = rule.rule_name.slice(rule.rule_name.indexOf('_') + 1);

    return (
      <CollapsiblePanelContainer
        panelStyle={this.state.hasUnsavedChanges ? 'warning' : undefined}
        title={
          <div className="flex">
            <div className="flex-grow">
              {ruleName}
            </div>
            <ConditionalComponent condition={this.state.hasUnsavedChanges}>
              <text style={{ paddingRight: '50px' }}> Unsaved Changes </text>
            </ConditionalComponent>
            <ConditionalComponent condition={isFavorite}>
              <text style={{ paddingRight: '10px' }}> {unicode.STAR} </text>
            </ConditionalComponent>
            <ConditionalComponent condition={isSubscribed}>
              <div style={{ color: colours.colours.green2 }}>
                {unicode.CIRCLE_HOLED}
              </div>
            </ConditionalComponent>
          </div>
        }
      >
        <fieldset>
          <LocalForm
            id={rule.rule_name + 'form'}
            model="info"
            initialState={initialState}
            onChange={(model) => this.setState(
              { hasUnsavedChanges: this.checkUnsavedChanges(request, model) }
            )}
            onSubmit={(model) => {
              this.setState({ hasUnsavedChanges: false });
              onSubmit(model);
            }}
            validators={{
              '' : {
                invalidCron: (model) =>
                  componentHelper.validateNotificationCron(model),
                noTypes: (model) => model.ruleTypes > 0
              }
            }}
          >
            <FormGroup>
              <Row>
                <Col md={6}>
                  {rule.description}
                </Col>
                <Col mdOffset={4} md={2}>
                  <FavoriteCheckBox style={{ display: 'inline' }} />
                </Col>
              </Row>
              <Row>
                <legend style={{ paddingLeft: '10px', marginTop: '10px' }}>
                  Types
                </legend>
                <SummaryCheckbox style={{ marginLeft: '10px' }} />
              </Row>
              <Row>
                <DetailedCheckbox style={{ marginLeft: '10px' }} />
              </Row>
              <Row>
                <Col>
                  <EmailCheckbox
                    style={{ display: 'inline', marginTop: '10px', marginLeft: '10px' }}
                  />
                  <CronInput
                    style={{
                      display: 'inline',
                      marginTop: '10px',
                      marginLeft: '35px',
                      width: '20%'
                    }}
                  />
                  <OverlayTrigger
                    trigger="click"
                    rootClose
                    overlay=
                      {getPopover(
                        'What\'s a cron?',
                        messaging.PROFILE_CRON_POPOVER_BODY)}
                  >
                    <Button style={{ display: 'inline' }}>?</Button>
                  </OverlayTrigger>
                </Col>
              </Row>
              <br />
              <Row>
                <legend style={{ paddingLeft: '10px' }}> Filters </legend>
                <MuteCheckBox style={{ marginLeft: '10px' }} />
              </Row>
              <Row>
                <Col>
                  <b style={{ paddingLeft: '10px' }}> Triggered By </b>
                  <TriggeredByInput
                    style={{ marginLeft: '10px', width: '50%' }}
                  />
                </Col>
              </Row>
              <br />
              <hr />
              <Row>
                <Col md={2}>
                  <Control.custom
                    id={'submit-btn'}
                    component={Button}
                    style={{ marginTop: '10px' }}
                    className="form-control"
                    model="."
                    type="submit"
                    mapProps={({ modelValue }) => {
                      const disabled =
                        !componentHelper.validateNotificationCron(modelValue) ||
                        modelValue.ruleTypes === 0;
                      return { disabled };
                    }}
                  >
                    {request ? 'Save Changes' : 'Subscribe'}
                  </Control.custom>
                </Col>
                <Col md={2} mdOffset={8}>
                  <Control.custom
                    id={'unsubscribe-btn'}
                    model="."
                    component={Button}
                    className="form-control"
                    style={{ marginTop: '10px' }}
                    disabled={!request}
                    mapProps={({ modelValue }) => {
                      return { onClick: () => onUnsubscribe(modelValue) };
                    }}
                  >
                    Unsubscribe
                  </Control.custom>
                </Col>
              </Row>
              <Errors
                className="popover-bottom"
                model="."
                style={{
                  color: colours.colours.red,
                  paddingTop: '10px'
                }}
                messages={{
                  invalidCron: 'Invalid Cron expression',
                  noTypes: 'At least one type must be selected.'
                }}
              />
            </FormGroup>
          </LocalForm>
        </fieldset>
      </CollapsiblePanelContainer>
    );
  }
}


// WEBPACK FOOTER //
// src/Components/NotificationsSelectorComponent.jsx